import { useState, useEffect } from "react";
import { Check, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { useBars } from "@/context/BarContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useQuery } from "@tanstack/react-query";

import abstractWaves from '../assets/backgrounds/abstract-waves.webp';
import purpleCosmos from '../assets/backgrounds/purple-cosmos.jpeg';
import retroSun from '../assets/backgrounds/retro-sun.jpeg';
import neonLeaves from '../assets/backgrounds/neon-leaves.jpeg';
import cityNight from '../assets/backgrounds/city-night.jpeg';
import holographicMoney from '../assets/backgrounds/holographic-money.jpeg';
import rainAlley from '../assets/backgrounds/rain-alley.jpeg';
import nowhereStation from '../assets/backgrounds/nowhere-station.jpeg';
import neonSign from '../assets/backgrounds/neon-sign.jpeg';
import neonFace from '../assets/backgrounds/neon-face.jpeg';
import cyberSkull from '../assets/backgrounds/cyber-skull.jpeg';
import vaporwaveStatue from '../assets/backgrounds/vaporwave-statue.jpeg';

export interface Background {
  id: string;
  name: string;
  image: string | null;
  unlockLevel: number;
  unlockDescription?: string;
  fallbackGradient?: string;
}

export const IMAGE_BACKGROUNDS: Background[] = [
  { 
    id: 'default', 
    name: 'Default', 
    image: null, 
    unlockLevel: 0,
    fallbackGradient: 'radial-gradient(ellipse at 50% 50%, rgba(168, 85, 247, 0.35) 0%, transparent 40%), radial-gradient(ellipse at center, #3d2066 0%, #1a1a2e 35%, #0f0f1a 100%)'
  },
  { id: 'abstract-waves', name: 'Abstract Waves', image: abstractWaves, unlockLevel: 0 },
  { id: 'purple-cosmos', name: 'Purple Cosmos', image: purpleCosmos, unlockLevel: 0 },
  { id: 'retro-sun', name: 'Retro Sun', image: retroSun, unlockLevel: 0 },
  { id: 'neon-leaves', name: 'Neon Leaves', image: neonLeaves, unlockLevel: 0 },
  { id: 'city-night', name: 'City Night', image: cityNight, unlockLevel: 0 },
  { id: 'holographic-money', name: 'Holographic', image: holographicMoney, unlockLevel: 0 },
  { id: 'rain-alley', name: 'Rain Alley', image: rainAlley, unlockLevel: 0 },
  { id: 'nowhere-station', name: 'Nowhere', image: nowhereStation, unlockLevel: 0 },
  { id: 'neon-sign', name: 'Neon Sign', image: neonSign, unlockLevel: 0 },
  { id: 'neon-face', name: 'Neon Face', image: neonFace, unlockLevel: 0 },
  { id: 'cyber-skull', name: 'Cyber Skull', image: cyberSkull, unlockLevel: 0 },
  { id: 'vaporwave-statue', name: 'Vaporwave', image: vaporwaveStatue, unlockLevel: 0 },
];

const STORAGE_KEY = "orphanbars-background";
const DEFAULT_BACKGROUND_ID = "purple-cosmos";

export function useBackground() {
  const { resolvedTheme } = useTheme();
  const [selectedId, setSelectedId] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem(STORAGE_KEY) || DEFAULT_BACKGROUND_ID;
    }
    return DEFAULT_BACKGROUND_ID;
  });

  const { data: siteSettings } = useQuery<Record<string, string>>({
    queryKey: ["background-site-settings"],
    queryFn: async () => {
      const res = await fetch("/api/backgrounds/site-settings");
      if (!res.ok) return {};
      return res.json();
    },
    staleTime: 300_000,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const configuredDefault = siteSettings?.defaultBackground;
    if (!configuredDefault) return;

    const storedId = localStorage.getItem(STORAGE_KEY);
    const shouldApplySiteDefault = !storedId || storedId === "default";

    if (shouldApplySiteDefault && storedId !== configuredDefault) {
      setSelectedId(configuredDefault);
    }
  }, [siteSettings?.defaultBackground]);

  // Fetch custom backgrounds
  const { data: customBackgrounds = [] } = useQuery({
    queryKey: ["backgrounds"],
    queryFn: async () => {
      const res = await fetch("/api/backgrounds");
      if (!res.ok) throw new Error("Failed to fetch backgrounds");
      return res.json();
    },
    staleTime: 300_000, // 5 minutes
  });

  // Combine built-in and custom backgrounds
  const allBackgrounds = [
    ...IMAGE_BACKGROUNDS,
    ...customBackgrounds.map((bg: any) => ({
      id: bg.sid || bg.id,
      name: bg.name,
      image: bg.imageUrl,
      unlockLevel: 0,
      isCustom: true,
    })),
  ];

  const selectedBackground =
    allBackgrounds.find((bg) => bg.id === selectedId) ||
    allBackgrounds.find((bg) => bg.id === DEFAULT_BACKGROUND_ID) ||
    allBackgrounds[0];

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, selectedId);
    
    const existingBgElement = document.getElementById('app-background-image');
    const rootElement = document.getElementById('root');
    if (existingBgElement && rootElement) {
      rootElement.removeChild(existingBgElement);
    }

    // Reset any previous inline overrides and let theme CSS drive defaults.
    document.body.style.removeProperty('background');
    document.body.style.removeProperty('background-attachment');
    document.body.style.removeProperty('background-color');

    if (selectedBackground.image) {
      document.documentElement.classList.add('has-custom-bg');
      document.documentElement.style.background = 'transparent';
      document.documentElement.style.backgroundColor = 'transparent';

      const bgElement = document.createElement('div');
      bgElement.id = 'app-background-image';
      bgElement.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: -1;
        background-image: url(${selectedBackground.image});
        background-size: cover;
        background-position: center;
        background-attachment: fixed;
        filter: blur(8px) brightness(0.68) saturate(1.05);
        transform: scale(1.08);
        opacity: 0.9;
        pointer-events: none;
      `;
      // Add CSS to ensure root element allows background positioning
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.style.position = 'relative';
        rootElement.style.minHeight = '100vh';
        rootElement.appendChild(bgElement);
      }

      // Remove the body background overlay to let the custom background show through
      document.body.style.background = 'transparent';
      document.body.style.backgroundAttachment = 'initial';
    } else {
      document.documentElement.classList.remove('has-custom-bg');
      document.documentElement.style.removeProperty('background');
      document.documentElement.style.removeProperty('background-color');
    }
  }, [selectedId, selectedBackground, resolvedTheme]);

  const setBackground = (id: string) => {
    setSelectedId(id);
  };

  const isUnlocked = (background: Background, userLevel: number) => {
    return userLevel >= background.unlockLevel;
  };

  return {
    selectedId,
    setSelectedId,
    setBackground,
    selectedBackground,
    allBackgrounds,
    isUnlocked: (bg: Background) => true, // All backgrounds are unlocked
    backgrounds: allBackgrounds,
  };
}

export function BackgroundSelector() {
  const { selectedId, setBackground, isUnlocked, backgrounds } = useBackground();
  const { currentUser } = useBars();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {backgrounds.map((bg) => {
          const isSelected = selectedId === bg.id;
          
          return (
            <button
              key={bg.id}
              onClick={() => setBackground(bg.id)}
              className={cn(
                "relative aspect-[3/4] rounded-xl overflow-hidden transition-all duration-200",
                "border-2",
                isSelected 
                  ? "border-primary ring-2 ring-primary/40 scale-[1.03]" 
                  : "border-foreground/15 hover:border-foreground/30 hover:scale-[1.02]",
              )}
              data-testid={`background-${bg.id}`}
            >
              {bg.image ? (
                <img 
                  src={bg.image} 
                  alt={bg.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div 
                  className="w-full h-full"
                  style={{ background: bg.fallbackGradient }}
                />
              )}
              
              {isSelected && (
                <div className="absolute top-1 right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center shadow-lg">
                  <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                </div>
              )}
              
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-1.5 pt-4">
                <span className="text-[9px] text-foreground/90 font-medium truncate block text-center">
                  {bg.name}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
